import useAhaTeachingCheck from "../../components/certificates/useAhaTeachingCheck";
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Alert, Select, MenuItem } from "@mui/material";
import { updateClass } from "../../api/backend";
import { useTenant } from "../../contexts/TenantContext";
import SessionStaffingPanel, { getInstructorName } from "./SessionStaffingPanel";

const C = {
  ink: "var(--color-ink)",
  muted: "var(--color-accent-violet-mid)",
  line: "var(--color-hairline-cloud)",
  lineStrong: "var(--color-hairline-cool)",
  success: "var(--color-success)",
  primary: "var(--color-primary)",
};

const PERMS = ["Take attendance", "Issue sign-offs", "Edit course content", "View roster"];

function initials(name = "") {
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}

function Check() {
  return (
    <Box component="svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" sx={{ color: C.success }}>
      <path d="M20 6 9 17l-5-5" />
    </Box>
  );
}

const outlineBtn = {
  height: 30, px: "10px", borderRadius: "8px", border: `1px solid ${C.lineStrong}`, bgcolor: "#fff",
  font: "600 11px var(--font-ui)", letterSpacing: ".2px", textTransform: "uppercase", color: C.ink, cursor: "pointer",
  "&:hover": { borderColor: C.ink, bgcolor: "var(--color-surface-press-light)" },
};

export default function CoordinatorInstructorsTab({ course, canEdit, courseInstructors = [], sessions = [], onRefresh }) {
  // An AHA class can only be taught by instructors with an AHA instructor ID in this center.
  const aha = useAhaTeachingCheck(course?.id);
  const { id } = useParams();
  const { user } = useTenant();

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState("");
  const [picking, setPicking] = useState(false);
  const [pendingLead, setPendingLead] = useState("");

  // courseInstructors = every instructor/coordinator in the active tenant,
  // not just this course's roster — any of them can hold the lead slot or a
  // per-session opening.
  const instructorMap = useMemo(
    () => Object.fromEntries((courseInstructors || []).map((u) => [u.id, u])),
    [courseInstructors],
  );

  const [leadId, setLeadId] = useState(course?.leadInstructorId || null);
  useEffect(() => { setLeadId(course?.leadInstructorId || null); }, [course]);

  const leadUser = instructorMap[leadId] || null;
  const leadName = leadUser ? (getInstructorName(leadUser) || "Unknown") : null;

  const startPicking = () => { setPendingLead(leadId || ""); setPicking(true); };

  const saveLead = async () => {
    const newLead = pendingLead ? Number(pendingLead) : null;
    const previous = leadId;
    setLeadId(newLead);
    setPicking(false);
    setSaving(true);
    setErr(null);
    try {
      await updateClass(user?.tenantId, id, { leadInstructorId: newLead });
      setSuccess("Saved");
      setTimeout(() => setSuccess(""), 2500);
      onRefresh?.();
    } catch (e) {
      setLeadId(previous);
      setErr(e?.response?.data?.message || e.message || "Failed to save.");
    } finally { setSaving(false); }
  };

  const showPicker = canEdit && (picking || !leadUser);

  return (
    <Box>
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "12px" }}>{err}</Alert>}

      <Box component="section">
        <Box sx={{ mb: "12px" }}>
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, color: C.ink }}>Lead instructor</Typography>
          <Typography sx={{ mt: "2px", fontSize: 13, color: C.muted }}>
            Responsible for the class overall. Can take attendance, issue sign-offs, edit course content, and view the roster.
          </Typography>
        </Box>

        <Box sx={{ bgcolor: "#fff", border: `1px solid ${C.line}`, borderRadius: "16px", boxShadow: "var(--shadow-card-sm)" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "44px minmax(0,1fr)", sm: "44px minmax(0,1fr) auto" },
            gap: "16px", alignItems: "center", p: "20px" }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: C.primary, color: "#fff", display: "grid",
              placeItems: "center", fontWeight: 700, fontSize: 14 }}>
              {leadName ? initials(leadName) : "?"}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box component="h3" sx={{ m: "0 0 2px", fontSize: 16, fontWeight: 600 }}>{leadName || "No lead instructor assigned"}</Box>
              {leadUser && (
                <>
                  <Box sx={{ fontSize: 13, color: C.muted }}>
                    {leadUser.roles?.includes("COURSE_COORDINATOR") ? "Course coordinator" : "Instructor"}
                    {leadUser.email ? ` · ${leadUser.email}` : ""}
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", mt: "10px", fontSize: 12.5 }}>
                    {PERMS.map((p) => (
                      <Box key={p} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Check />{p}</Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
            {canEdit && leadUser && !picking && (
              <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                <Box component="button" type="button" sx={outlineBtn} onClick={startPicking} disabled={saving}>Change</Box>
              </Box>
            )}
          </Box>

          {showPicker && (
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", p: { xs: "0 20px 20px", sm: "0 20px 20px 80px" } }}>
              <Select size="small" displayEmpty fullWidth={false} value={pendingLead}
                inputProps={{ "aria-label": "Lead instructor" }}
                disabled={saving}
                onChange={(e) => setPendingLead(e.target.value)}
                renderValue={(v) => v ? getInstructorName(instructorMap[v]) : <span style={{ color: C.muted }}>Choose lead instructor…</span>}
                sx={{ minWidth: 260, height: 36, fontSize: 14 }}>
                <MenuItem value=""><em>No lead instructor</em></MenuItem>
                {courseInstructors.map((u) => <MenuItem key={u.id} value={u.id}>{getInstructorName(u) || `#${u.id}`}</MenuItem>)}
              </Select>
              <Box component="button" type="button" onClick={saveLead} disabled={saving}
                sx={{ ...outlineBtn, height: 36, bgcolor: C.primary, color: "#fff", borderColor: C.primary,
                  "&:hover": { bgcolor: "var(--color-ink-deep)" } }}>Save</Box>
              {leadUser && <Box component="button" type="button" sx={{ ...outlineBtn, height: 36 }} onClick={() => setPicking(false)}>Cancel</Box>}
            </Box>
          )}
        </Box>

        {success && (
          <Box sx={{ mt: "8px", fontSize: 12.5, fontWeight: 600, color: C.success, textAlign: "right" }}>{success}</Box>
        )}
      </Box>

      <SessionStaffingPanel sessions={sessions} instructorMap={instructorMap} canEdit={canEdit}
        courseInstructors={courseInstructors} blocked={aha.blocked} />
    </Box>
  );
}
